import { Component, OnInit, OnDestroy, ViewChild, ElementRef, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ReactiveFormsModule, FormControl } from '@angular/forms';

import { TableModule, Table, TablePageEvent } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ConfirmationService, MessageService } from 'primeng/api';

import { Api } from '../../../api/api';
import {
  adminUsersControllerGetAllAdminUsers,
  AdminUsersControllerGetAllAdminUsers$Params,
  adminUsersControllerDeleteAdminUser,
} from '../../../api/functions';
import { UserResponseAdminAdminDto } from '../../../api/models/user-response-admin-admin-dto';
import { HeaderService } from '../../../services/header.service';
import { debounceTime, distinctUntilChanged, Subject, takeUntil, skip } from 'rxjs';

@Component({
  selector: 'app-admin-users',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    ConfirmDialogModule,
    IconFieldModule,
    InputIconModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './admin-users.component.html',
  styleUrl: './admin-users.component.css',
})
export class AdminUsersComponent implements OnInit, OnDestroy {
  @ViewChild('table') table!: Table;
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  private readonly api = inject(Api);
  private readonly router = inject(Router);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly headerService = inject(HeaderService);

  protected readonly adminUsers = signal<UserResponseAdminAdminDto[]>([]);
  protected readonly loading = signal(false);
  protected readonly totalRecords = signal(0);
  protected readonly searchControl = new FormControl('');

  private readonly destroy$ = new Subject<void>();
  private isSearching = false;

  protected readonly queryParams = signal<AdminUsersControllerGetAllAdminUsers$Params>({
    page: 1,
    limit: 10,
    sortBy: 'id',
    sortOrder: 'asc',
  });

  protected first = 0;
  protected readonly rowsPerPageOptions = [10, 25, 50, 100];

  constructor() {}

  ngOnInit(): void {
    this.headerService.setTitle(this.translateService.instant('adminUsers.title'));
    this.headerService.setSubtitle(this.translateService.instant('adminUsers.subtitle'));

    // Load initial data
    this.loadAdminUsers();

    // Setup search with debounce
    this.searchControl.valueChanges
      .pipe(
        skip(1), // Skip initial value emission
        takeUntil(this.destroy$),
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe((searchTerm) => {
        // Mark that we're searching to restore focus later
        this.isSearching = true;

        // Reset to first page when searching
        this.first = 0;
        const params = this.queryParams();
        this.queryParams.set({
          ...params,
          page: 1,
          search: searchTerm || undefined,
        });
        this.loadAdminUsers();
      });
  }

  protected onPageChange(event: TablePageEvent): void {
    const page = Math.floor((event.first || 0) / (event.rows || this.queryParams().limit!)) + 1;
    const limit = event.rows || this.queryParams().limit!;

    this.first = event.first || 0;

    // Preserve search term when paginating
    const currentParams = this.queryParams();
    this.queryParams.set({
      page,
      limit,
      sortBy: currentParams.sortBy,
      sortOrder: currentParams.sortOrder,
      search: currentParams.search,
    });

    this.loadAdminUsers();
  }

  private loadAdminUsers(): void {
    this.loading.set(true);
    this.searchControl.disable();

    this.api
      .invoke(adminUsersControllerGetAllAdminUsers, this.queryParams())
      .then((response) => {
        this.adminUsers.set(response.data);
        this.totalRecords.set(response.meta.totalItems);
      })
      .catch(() => {
        this.adminUsers.set([]);
        this.totalRecords.set(0);
      })
      .finally(() => {
        this.loading.set(false);
        this.searchControl.enable();

        // Restore focus to search input if we were searching
        if (this.isSearching && this.searchInput) {
          setTimeout(() => {
            this.searchInput.nativeElement.focus();
            this.isSearching = false;
          }, 0);
        }
      });
  }

  protected editAdminUser(adminUser: UserResponseAdminAdminDto): void {
    this.router.navigate(['/admin-users', adminUser.id]);
  }

  protected addAdminUser(): void {
    this.router.navigate(['/admin-users', 'new']);
  }

  protected refreshAdminUsers(): void {
    const params = this.queryParams();
    this.queryParams.set({
      ...params,
      page: Math.floor(this.first / params.limit!) + 1,
    });
    this.loadAdminUsers();
  }

  protected deleteAdminUser(adminUser: UserResponseAdminAdminDto): void {
    this.confirmationService.confirm({
      message: this.translateService.instant('adminUsers.confirmDelete.message', {
        username: adminUser.username,
      }),
      header: this.translateService.instant('adminUsers.confirmDelete.header'),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-outlined',
      acceptLabel: this.translateService.instant('adminUsers.confirmDelete.confirm'),
      rejectLabel: this.translateService.instant('adminUsers.confirmDelete.cancel'),
      accept: () => {
        this.api
          .invoke(adminUsersControllerDeleteAdminUser, { id: adminUser.id })
          .then(() => {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('adminUsers.success.deleteTitle'),
              detail: this.translateService.instant('adminUsers.success.deleteMessage', {
                username: adminUser.username,
              }),
              life: 4000,
            });
            this.refreshAdminUsers();
          })
          .catch(() => {});
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
